/**
 * Interface language.
 *
 * Adding a language is deliberately a two-line job: drop a JSON file next to
 * the others and add a row to LANGUAGES. Nothing else in the app names a
 * language, so no component has to change.
 *
 * Which language a visitor gets:
 *   - on an edition that serves one market, that market's language, full stop —
 *     no picker, nothing overrides it;
 *   - otherwise what the account says: their own pick (`users.language`), or
 *     failing that the language of their regional office (`document_language`,
 *     already the source of truth for invoices and certificates — the interface
 *     should not disagree with the paperwork);
 *   - failing that, English.
 *
 * The choice belongs to the account, not to the browser: the same person gets
 * the same interface on a laptop and a phone. localStorage still holds the last
 * language seen, but only so the login screen and the first paint are right
 * before /auth/me answers — once it does, the account wins.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { API_BASE_URL } from '../config/api';

import en from './locales/en.json';
import uk from './locales/uk.json';
import ru from './locales/ru.json';
import es from './locales/es.json';
import kk from './locales/kk.json';
import pl from './locales/pl.json';
import de from './locales/de.json';
import fr from './locales/fr.json';
import it from './locales/it.json';
import cs from './locales/cs.json';
import tr from './locales/tr.json';
import uz from './locales/uz.json';

export interface LanguageDef {
  code: string;
  /** Written in the language itself — a picker in English helps nobody. */
  native: string;
  /** For reading dates and numbers; not every language maps 1:1 to one locale. */
  locale: string;
}

/**
 * English first because it is the fallback; the rest by their own name, Latin
 * script before Cyrillic. A reader scans a picker for the word they recognise,
 * so the list is ordered the way it is written, not by code.
 */
export const LANGUAGES: LanguageDef[] = [
  { code: 'en', native: 'English',    locale: 'en-GB' },
  { code: 'cs', native: 'Čeština',    locale: 'cs-CZ' },
  { code: 'de', native: 'Deutsch',    locale: 'de-DE' },
  { code: 'es', native: 'Español',    locale: 'es-ES' },
  { code: 'fr', native: 'Français',   locale: 'fr-FR' },
  { code: 'it', native: 'Italiano',   locale: 'it-IT' },
  { code: 'pl', native: 'Polski',     locale: 'pl-PL' },
  { code: 'tr', native: 'Türkçe',     locale: 'tr-TR' },
  { code: 'uz', native: 'Oʻzbekcha',  locale: 'uz-UZ' },
  { code: 'ru', native: 'Русский',    locale: 'ru-RU' },
  { code: 'uk', native: 'Українська', locale: 'uk-UA' },
  { code: 'kk', native: 'Қазақша',    locale: 'kk-KZ' },
];

const RESOURCES: Record<string, any> = { en, uk, ru, es, kk, pl, de, fr, it, cs, tr, uz };
const STORAGE_KEY = 'sense.language';
/**
 * Editions that serve a single market speak that market's language and offer no
 * choice: no picker, and neither a stored pick nor the account's office can
 * override it. Everything else is multilingual and the visitor decides.
 */
const EDITION_LANGUAGE: Record<string, string> = { dievo: 'uk' };

let _edition = '';
export const languageLocked = (): boolean => _edition in EDITION_LANGUAGE;

export const isSupported = (code?: string | null): boolean =>
  !!code && LANGUAGES.some(l => l.code === code);

/** Normalises 'uk-UA' / 'UK' to a code we actually carry, or undefined. */
const normalise = (raw?: string | null): string | undefined => {
  const code = (raw || '').trim().toLowerCase().split(/[-_]/)[0];
  return isSupported(code) ? code : undefined;
};

export const storedLanguage = (): string | undefined => {
  try {
    return normalise(localStorage.getItem(STORAGE_KEY));
  } catch {
    return undefined; // private window, blocked storage — fall through
  }
};

export function resolveLanguage(edition?: string): string {
  const fixed = EDITION_LANGUAGE[edition || ''];
  return fixed || storedLanguage() || 'en';
}

/** Applies a language to the running app. Does not decide anything. */
function use(code: string): void {
  void i18n.changeLanguage(code);
  document.documentElement.lang = code;
  try { localStorage.setItem(STORAGE_KEY, code); } catch { /* not fatal */ }
}

/**
 * The visitor chose this one. Saved on the account, so it follows them to the
 * next browser; the local copy is written first so the interface switches at
 * once and the next first paint is already right.
 *
 * A failed save is deliberately not surfaced: the language has visibly changed
 * either way, and the next successful save or sign-in reconciles it. Signed out
 * (no token) there is nothing to save to and the local copy is the whole story.
 */
export function setLanguage(code: string): void {
  if (languageLocked() || !isSupported(code)) return;
  use(code);
  let token: string | null = null;
  try { token = localStorage.getItem('access_token'); } catch { /* blocked */ }
  if (!token) return;
  void fetch(`${API_BASE_URL}/auth/profile`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    credentials: 'include',
    body: JSON.stringify({ language: code }),
  }).catch(() => { /* offline — the local copy still holds */ });
}

/**
 * The language /auth/me reports for this account: their own pick, or their
 * office's language. It wins over whatever this browser last saw — that copy
 * may belong to a different account that signed in here before.
 */
export function applyAccountLanguage(code?: string | null): void {
  const account = normalise(code);
  if (languageLocked() || !account || account === i18n.language) return;
  use(account);
}

/**
 * A targeting value named in the interface language. The English string IS the
 * value we store and hand to the platform, so this is display only: unknown
 * values come back unchanged.
 */
export const targetingLabel = (value: string): string =>
  i18n.t(`targetingValues.${value}`, { defaultValue: value });

export const currentLocale = (): string =>
  (LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0]).locale;

export function initI18n(edition?: string): void {
  _edition = edition || '';
  const lng = resolveLanguage(edition);
  void i18n.use(initReactI18next).init({
    resources: Object.fromEntries(
      Object.entries(RESOURCES).map(([code, translation]) => [code, { translation }]),
    ),
    lng,
    fallbackLng: 'en',          // a key missing from a translation falls back, never blank
    returnNull: false,
    interpolation: { escapeValue: false },  // React escapes already
  });
  document.documentElement.lang = lng;
}

export default i18n;
